import { 
  BarChart3, 
  Calendar, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  Brain,
  Building2,
  Zap,
  User,
  PlusCircle,
  FileText,
  Package,
  Truck,
  Settings,
  UserPlus,
  CheckSquare,
  ClipboardList,
  Plus
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

interface DashboardItemsConfig {
  dashboardHeader: boolean;
  worksSummary: boolean;
  quickActions: boolean;
  quickActionsRelatorio: boolean;
  quickActionsRDO: boolean;
  quickActionsTarefas: boolean;
  quickActionsSugestoes: boolean;
  quickActionsCadastros: boolean;
  quickActionsNovaObra: boolean;
  recentAlerts: boolean;
  upcomingTasks: boolean;
  aiSuggestions: boolean;
}

interface DashboardItemsPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: DashboardItemsConfig;
  onConfigChange: (config: DashboardItemsConfig) => void;
}

const dashboardItems = [
  {
    title: "Cabeçalho e Navegação",
    items: [
      {
        key: "dashboardHeader" as keyof DashboardItemsConfig,
        title: "Cabeçalho do Dashboard",
        description: "Informações do usuário e data atual",
        icon: User,
      },
    ],
  },
  {
    title: "Resumo e Atalhos",
    items: [
      {
        key: "worksSummary" as keyof DashboardItemsConfig,
        title: "Resumo das Obras",
        description: "Visão geral das obras em andamento",
        icon: Building2,
      },
      {
        key: "quickActions" as keyof DashboardItemsConfig,
        title: "Atalhos Rápidos",
        description: "Acesso rápido a ações principais",
        icon: Zap,
        subItems: [
          {
            key: "quickActionsRelatorio" as keyof DashboardItemsConfig,
            title: "Preencher RDO",
            description: "Relatório diário de obra",
            icon: FileText,
          },
          {
            key: "quickActionsRDO" as keyof DashboardItemsConfig,
            title: "Aprovar RDOs",
            description: "5 pendentes",
            icon: CheckSquare,
          },
          {
            key: "quickActionsTarefas" as keyof DashboardItemsConfig,
            title: "Acessar Tarefas",
            description: "3 para hoje",
            icon: ClipboardList,
          },
          {
            key: "quickActionsSugestoes" as keyof DashboardItemsConfig,
            title: "Sugestões da IA",
            description: "2 novas sugestões",
            icon: Brain,
          },
          {
            key: "quickActionsCadastros" as keyof DashboardItemsConfig,
            title: "Cadastros",
            description: "Acessar cadastros",
            icon: UserPlus,
          },
          {
            key: "quickActionsNovaObra" as keyof DashboardItemsConfig,
            title: "Nova Obra",
            description: "Criar projeto",
            icon: Plus,
          }
        ]
      },
    ],
  },
  {
    title: "Alertas e Tarefas",
    items: [
      {
        key: "recentAlerts" as keyof DashboardItemsConfig,
        title: "Alertas Recentes",
        description: "Notificações e alertas importantes",
        icon: AlertTriangle,
      },
      {
        key: "upcomingTasks" as keyof DashboardItemsConfig,
        title: "Próximas Tarefas",
        description: "Tarefas agendadas e pendentes",
        icon: CheckCircle,
      },
    ],
  },
  {
    title: "Inteligência Artificial",
    items: [
      {
        key: "aiSuggestions" as keyof DashboardItemsConfig,
        title: "Sugestões da IA",
        description: "Recomendações inteligentes do sistema",
        icon: Brain,
      },
    ],
  },
];

export const DashboardItemsPopup = ({ 
  open, 
  onOpenChange, 
  config, 
  onConfigChange 
}: DashboardItemsPopupProps) => {
  const handleToggle = (key: keyof DashboardItemsConfig, value: boolean) => {
    onConfigChange({
      ...config,
      [key]: value,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Configurar Dashboard</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {dashboardItems.map((category, categoryIndex) => (
            <div key={category.title} className="space-y-4">
              <h3 className="font-medium text-foreground">{category.title}</h3>
              
              <div className="space-y-4">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.key} className="space-y-3">
                      <div className="flex items-start space-x-3 p-3 rounded-lg border">
                        <Icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <Label 
                              htmlFor={item.key}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {item.title}
                            </Label>
                            <Switch
                              id={item.key}
                              checked={config[item.key]}
                              onCheckedChange={(checked) => handleToggle(item.key, checked)}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Render subItems if they exist and parent is enabled */}
                      {(item as any).subItems && config[item.key] && (
                        <div className="ml-8 space-y-2">
                          {(item as any).subItems.map((subItem: any) => {
                            const SubIcon = subItem.icon;
                            return (
                              <div key={subItem.key} className="flex items-center space-x-2 p-2 rounded border border-dashed bg-muted/30">
                                <SubIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <Label 
                                      htmlFor={subItem.key}
                                      className="text-xs font-medium cursor-pointer truncate"
                                    >
                                      {subItem.title}
                                    </Label>
                                    <Switch
                                      id={subItem.key}
                                      checked={config[subItem.key]}
                                      onCheckedChange={(checked) => handleToggle(subItem.key, checked)}
                                      className="scale-75"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {categoryIndex < dashboardItems.length - 1 && <Separator />}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export type { DashboardItemsConfig };